import moment from 'moment';
import { useMemo, useState } from 'react';

import { DateOptionSelectOption } from 'Models/Dashboards';

export type DateFilterSelectionCustom = DateOptionSelectOption.CUSTOM;
export type DateFilterSelectionPreset = Exclude<DateOptionSelectOption, DateOptionSelectOption.CUSTOM>;

export interface DateRange {
    startDate: Date;
    endDate: Date;
}

type DateFilterSelection =
    | {
          option: DateFilterSelectionPreset;
      }
    | {
          option: DateFilterSelectionCustom;
          dateRange: DateRange;
      };

const getDateRangeFromPreset = (preset: DateFilterSelectionPreset): DateRange => {
    const nowUtc = moment().toDate();

    switch (preset) {
        case DateOptionSelectOption.LAST_YEAR:
            return { startDate: moment().subtract(1, 'year').startOf('month').toDate(), endDate: nowUtc };
        case DateOptionSelectOption.LAST_SIX_MONTHS:
            return { startDate: moment().subtract(6, 'months').startOf('month').toDate(), endDate: nowUtc };
        case DateOptionSelectOption.LAST_THREE_MONTHS:
            return { startDate: moment().subtract(3, 'months').startOf('month').toDate(), endDate: nowUtc };
        case DateOptionSelectOption.NEXT_ONE_MONTH:
            return { startDate: nowUtc, endDate: moment().add(1, 'months').endOf('month').toDate() };
        case DateOptionSelectOption.NEXT_THREE_MONTHS:
            return { startDate: nowUtc, endDate: moment().add(3, 'months').endOf('month').toDate() };
        case DateOptionSelectOption.NEXT_SIX_MONTHS:
            return { startDate: nowUtc, endDate: moment().add(6, 'months').endOf('month').toDate() };
        case DateOptionSelectOption.NEXT_YEAR:
            return { startDate: nowUtc, endDate: moment().add(1, 'year').endOf('month').toDate() };
    }
};

export interface DateFilterHookValues {
    selection: {
        option: DateOptionSelectOption;
        dateRange: DateRange;
    };
    updateSelection: (selection: DateFilterSelection) => void;
}

export const useDateFilter = (defaultPreset: DateFilterSelectionPreset): DateFilterHookValues => {
    const [option, setOption] = useState<DateOptionSelectOption>(defaultPreset);
    const [dateRange, setDateRange] = useState<DateRange>(getDateRangeFromPreset(defaultPreset));

    return useMemo(
        () => ({
            selection: {
                option,
                dateRange,
            },
            updateSelection: (selection: DateFilterSelection) => {
                setOption(selection.option);
                if (selection.option === DateOptionSelectOption.CUSTOM) {
                    setDateRange(selection.dateRange);
                } else {
                    setDateRange(getDateRangeFromPreset(selection.option));
                }
            },
        }),
        [option, dateRange]
    );
};
